import { useMutation, useQuery } from '@apollo/client';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { PencilIcon } from '@heroicons/react/24/solid';
import { useLayoutContext } from '~/components/layout/Layout';
import { GET_AGENT_TRAINING_DATA, UPDATE_AGENT_PROMPTS } from '~/services';
import { PromptEditorDialog } from './PromptEditorDialog';

type Language = 'en' | 'es';

interface LanguageOption {
  code: Language;
  label: string;
}

// Skeleton loading component matching MemoriesModule style
const TrainingSettingsSkeleton = () => (
  <div className="animate-pulse">
    <div className="mb-4 h-6 w-1/3 rounded bg-gray-200"></div>
    <div className="mb-6 h-4 w-1/4 rounded bg-gray-200"></div>
    <div className="space-y-4">
      <div className="h-10 w-full max-w-xs rounded bg-gray-200"></div>
      <div className="h-32 w-full rounded bg-gray-200"></div>
      <div className="h-10 w-48 rounded bg-gray-200"></div>
    </div>
  </div>
);

// Helper function to convert HTML content to clean text with preserved line breaks
const htmlToCleanText = (htmlContent: string): string => {
  if (!htmlContent) return '';

  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;

  // Replace paragraph tags with line breaks
  const paragraphs = tempDiv.querySelectorAll('p');
  paragraphs.forEach((p) => {
    // Add double line break after each paragraph (except the last one)
    const isLastParagraph = p === paragraphs[paragraphs.length - 1];
    p.replaceWith(p.textContent + (isLastParagraph ? '' : '\n\n'));
  });

  // Replace br tags with single line breaks
  const breaks = tempDiv.querySelectorAll('br');
  breaks.forEach((br) => {
    br.replaceWith('\n');
  });

  // Get clean text content
  let cleanText = tempDiv.textContent || tempDiv.innerText || '';

  // Clean up excessive whitespace and line breaks
  cleanText = cleanText
    .replace(/\n\s*\n\s*\n/g, '\n\n') // Replace multiple line breaks with double
    .replace(/^\s+|\s+$/g, '') // Trim whitespace from start and end
    .replace(/[ \t]+/g, ' '); // Replace multiple spaces/tabs with single space

  return cleanText;
};

export const TrainingSettings = () => {
  const { agentId } = useParams();
  const [selectedLanguage, setSelectedLanguage] = useState<Language>('en');
  const [promptData, setPromptData] = useState<{ [key in Language]: string }>({
    en: '',
    es: '',
  });
  const [defaultLanguage, setDefaultLanguage] = useState<Language>('en');
  const [hasData, setHasData] = useState(false);
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [t] = useTranslation('training_settings');
  const { showToast: displayToast } = useLayoutContext();
  const dropdownRef = useRef<HTMLDetailsElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Available languages
  const languages: LanguageOption[] = [
    { code: 'en', label: t('english') },
    { code: 'es', label: t('spanish') },
  ];

  const { data, loading } = useQuery(GET_AGENT_TRAINING_DATA, {
    variables: {
      data: {
        agent_id: agentId,
      },
    },
    onCompleted: (data) => {
      const { agent } = data?.get_agent_training_data?.data || {};
      const prompts = agent?.configuration?.system_prompt || {};

      // Initialize prompt data with existing content
      const newPromptData = {
        en: prompts.en || '',
        es: prompts.es || '',
      };

      // Detect default language (the one with content, prioritizing en)
      let detectedDefault: Language = 'en';
      if (prompts.en && prompts.en.trim()) {
        detectedDefault = 'en';
      } else if (prompts.es && prompts.es.trim()) {
        detectedDefault = 'es';
      }

      setPromptData(newPromptData);
      setDefaultLanguage(detectedDefault);
      setSelectedLanguage(detectedDefault);
      setHasData(true);
    },
  });

  const [updateAgentPrompts] = useMutation(UPDATE_AGENT_PROMPTS, {
    onCompleted: () => {
      setToastMessage(t('toastMessage'));
      displayToast(t('toastMessage'));
    },
  });

  // Handle language selection change
  const handleLanguageChange = (language: Language) => {
    setSelectedLanguage(language);

    // Close the dropdown
    if (dropdownRef.current) {
      dropdownRef.current.removeAttribute('open');
    }
  };

  const handlePromptSave = async (updatedPromptData: {
    [key in Language]: string;
  }) => {
    setPromptData(updatedPromptData);

    // Save all language prompts to backend
    updateAgentPrompts({
      variables: {
        data: {
          prompt: updatedPromptData,
          agent_id: agentId,
        },
      },
    });
  };

  const openPromptEditor = () => {
    setShowPromptEditor(true);
  };

  if (loading) {
    return (
      <div className="flex w-full flex-col gap-4 lg:gap-6 lg:pr-20">
        <TrainingSettingsSkeleton />
      </div>
    );
  }

  const selectedLanguageLabel =
    languages.find((lang) => lang.code === selectedLanguage)?.label || '';

  return (
    <motion.div
      className="flex w-full flex-col gap-4 lg:gap-6 lg:pr-20"
      ref={containerRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{
        opacity: hasData ? 1 : 0.8,
        y: hasData ? 0 : 20,
      }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <h2 className="mb-4 pt-4 text-xl font-medium">{t('title')}</h2>

      {/* Default Language Indicator */}
      {hasData && (
        <div className="mb-4 rounded-lg bg-blue-50 p-3">
          <p className="text-sm text-blue-700">
            <span className="font-medium">
              {t('default_language', 'Default Language')}:
            </span>{' '}
            {languages.find((lang) => lang.code === defaultLanguage)?.label}
            {defaultLanguage !== selectedLanguage && (
              <span className="ml-2 text-blue-600">
                ({t('currently_editing', 'Currently editing')}:{' '}
                {selectedLanguageLabel})
              </span>
            )}
          </p>
        </div>
      )}

      {/* Language Selector Dropdown */}
      <div className="w-full max-w-xs">
        <label className="mb-2 block text-sm font-medium text-gray-700">
          {t('language_selector')}
        </label>
        <details ref={dropdownRef} className="dropdown">
          <summary className="btn btn-outline w-full justify-between">
            {selectedLanguageLabel}
            <svg
              className="ml-2 h-4 w-4 fill-current"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
            >
              <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
            </svg>
          </summary>
          <ul className="menu dropdown-content rounded-box z-[1] mt-1 w-full border border-gray-200 bg-white p-2 shadow-lg">
            {languages.map((language) => (
              <li key={language.code}>
                <button
                  type="button"
                  onClick={() => handleLanguageChange(language.code)}
                  className={`w-full px-4 py-2 text-left hover:bg-gray-100 ${
                    selectedLanguage === language.code
                      ? 'bg-gray-100 font-medium'
                      : ''
                  } ${
                    defaultLanguage === language.code
                      ? 'border-l-4 border-blue-500'
                      : ''
                  }`}
                >
                  {language.label}
                  {defaultLanguage === language.code && (
                    <span className="ml-2 text-xs text-blue-600">
                      ({t('default', 'Default')})
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </details>
      </div>

      {/* Prompt Content Section */}
      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          {t('prompt_label')} ({selectedLanguageLabel})
        </label>

        {/* Prompt Preview */}
        <div className="relative">
          <div className="min-h-[120px] w-full rounded-lg border border-gray-300 bg-gray-50 p-4 text-sm text-gray-700">
            {promptData[selectedLanguage] ? (
              <div className="whitespace-pre-line">
                {promptData[selectedLanguage].length > 200
                  ? `${htmlToCleanText(promptData[selectedLanguage]).substring(
                      0,
                      200,
                    )}...`
                  : htmlToCleanText(promptData[selectedLanguage])}
              </div>
            ) : (
              <div className="italic text-gray-400">
                {t(
                  'no_prompt_content',
                  'No prompt content for this language yet.',
                )}
              </div>
            )}
          </div>

          {/* Edit Button Overlay */}
          <button
            onClick={openPromptEditor}
            className="btn btn-primary btn-sm absolute right-2 top-2 rounded-full"
            title={t('edit_prompt', 'Edit Prompt')}
          >
            <PencilIcon className="mr-1 h-4 w-4" />
            {t('edit', 'Edit')}
          </button>
        </div>

        {/* Character count indicator */}
        <div className="text-xs text-gray-500">
          {promptData[selectedLanguage] && (
            <>
              {t('characters_count', 'Characters: {{count}}', {
                count: promptData[selectedLanguage].length,
              })}
              {promptData[selectedLanguage].length < 120 && (
                <span className="ml-2 text-orange-600">
                  (
                  {t(
                    'minimum_characters_recommended',
                    'Minimum {{min}} characters recommended',
                    { min: 120 },
                  )}
                  )
                </span>
              )}
              {promptData[selectedLanguage].length > 30000 && (
                <span className="ml-2 text-red-600">
                  (
                  {t('maximum_characters', 'Maximum {{max}} characters', {
                    max: 30000,
                  })}
                  )
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Prompt Editor Dialog */}
      <PromptEditorDialog
        isOpen={showPromptEditor}
        onClose={() => setShowPromptEditor(false)}
        promptData={promptData}
        onSave={handlePromptSave}
        agentId={agentId || ''}
        defaultLanguage={defaultLanguage}
        currentLanguage={selectedLanguage}
        onLanguageChange={handleLanguageChange}
      />
    </motion.div>
  );
};
